import { useMemo } from 'react';
import {
  AppleLogo,
  DotsThree,
  MagnifyingGlass,
  Plus,
  Stack,
  UploadSimple,
  WindowsLogo,
} from '@phosphor-icons/react';
import { Button, Dropdown, Flex, Input } from 'antd';
import { useFulfillmentTabActions } from '../../contexts/fulfillment-tab-actions';
import { ShipNodeDownloads } from '@skupreme/shared/components/shipnode';
import { Tabs } from '../../utility';
import { IconSizes } from '@utility/constants/style-contants';
import { shipNodeChannel } from '../../utility/shipnode/channel';
const F = Tabs.settingsTab.fulfillmentTabs;

const ADD_LABELS = {
  [F.waveRules]: 'Wave Rule',
  [F.totes]: 'Tote',
  [F.pallets]: 'Pallet',
  [F.packingStations]: 'Station',
};

const ADD_TABS = [F.waveRules, F.totes, F.pallets, F.packingStations];

/** Actions aligned to the right of Fulfillment sub-tabs. */
export function SettingsFulfillmentToolbar({
  currentTab,
  search,
  onSearchChange,
  onBulkUploadTotes,
  onBulkUploadPallets,
}) {
  const { onAdd, onBulkGeneratePallets, onBulkGenerateTotes } =
    useFulfillmentTabActions();
  const isPalletsTab = currentTab === F.pallets;
  const isTotesTab = currentTab === F.totes;
  const showAdd = ADD_TABS.includes(currentTab) && !isPalletsTab && !isTotesTab;
  const showPalletsActionsMenu = isPalletsTab;
  const showTotesActionsMenu = isTotesTab;
  const showSearch = [F.totes, F.pallets].includes(currentTab);
  const showDownloads = currentTab === F.packingStations;

  const totesMenuItems = useMemo(
    () => [
      {
        key: 'add-tote',
        label: 'Add Tote',
        icon: <Plus size={16} />,
        disabled: !onAdd,
        onClick: () => onAdd?.(),
      },
      {
        key: 'bulk-generate-tote',
        label: 'Bulk Generate Tote',
        icon: <Stack size={16} />,
        disabled: !onBulkGenerateTotes,
        onClick: () => onBulkGenerateTotes?.(),
      },
      {
        key: 'bulk-upload',
        label: 'Bulk Upload',
        icon: <UploadSimple size={16} />,
        onClick: onBulkUploadTotes,
      },
    ],
    [onAdd, onBulkGenerateTotes, onBulkUploadTotes],
  );

  const palletsMenuItems = useMemo(
    () => [
      {
        key: 'add-pallet',
        label: 'Add Pallet',
        icon: <Plus size={16} />,
        disabled: !onAdd,
        onClick: () => onAdd?.(),
      },
      {
        key: 'bulk-generate-pallet',
        label: 'Bulk Generate Pallet',
        icon: <Stack size={16} />,
        disabled: !onBulkGeneratePallets,
        onClick: () => onBulkGeneratePallets?.(),
      },
      {
        key: 'bulk-upload',
        label: 'Bulk Upload',
        icon: <UploadSimple size={16} />,
        onClick: onBulkUploadPallets,
      },
    ],
    [onAdd, onBulkGeneratePallets, onBulkUploadPallets],
  );

  /* The ShipNode (Electron) installers for this build's environment, as in Settings › ShipNode (EN-3277). */
  const channel = shipNodeChannel();
  const macHref = ShipNodeDownloads.mac[channel];
  const windowsHref = ShipNodeDownloads.windows[channel];

  return (
    <Flex align="center" gap={8} wrap="nowrap">
      {showAdd && (
        <Button
          type="primary"
          icon={<Plus size={16} />}
          onClick={() => onAdd?.()}
          disabled={!onAdd}
        >
          {ADD_LABELS[currentTab]}
        </Button>
      )}
      {showTotesActionsMenu && (
        <Dropdown
          menu={{ items: totesMenuItems }}
          trigger={['click']}
          placement="bottomRight"
        >
          <Button
            type="text"
            icon={<DotsThree size={18} weight="bold" />}
            aria-label="Tote actions"
          />
        </Dropdown>
      )}
      {showPalletsActionsMenu && (
        <Dropdown
          menu={{ items: palletsMenuItems }}
          trigger={['click']}
          placement="bottomRight"
        >
          <Button
            type="text"
            icon={<DotsThree size={18} weight="bold" />}
            aria-label="Pallet actions"
          />
        </Dropdown>
      )}
      {showSearch && (
        <Input
          placeholder={currentTab === F.totes ? 'Tote Name' : 'Pallet Name'}
          value={search ?? ''}
          onChange={e => onSearchChange(e.target.value)}
          allowClear
          prefix={<MagnifyingGlass size={IconSizes.small} />}
          style={{ width: 200 }}
        />
      )}
      {showDownloads && (
        <>
          <Button href={macHref} icon={<AppleLogo size={16} />}>
            Download for Mac
          </Button>
          <Button href={windowsHref} icon={<WindowsLogo size={16} />}>
            Download for Windows
          </Button>
        </>
      )}
    </Flex>
  );
}
